import React, {Component} from 'react';
import {AutoSizer} from 'react-virtualized';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import _ from 'lodash';
import cx from 'classnames';
import {DataGrid, Input, Button} from '@perion-undertone/ut-react-common';
import ImportFile from '../ImportFile';
import BulkActionsOverlay from '../../../grid/BulkActionsOverlay';
import {adslotsGridSchema} from './../grid/adSlotsGridSchema';
import {
    createNewPlacementGroupAdSlot,
    searchAdSlots,
    textProvider
} from '../imports';
import {filterAdSlots} from './adSlotsUtils';
import {adaptColumnsForV7, SelectColumn} from '../../../../utils/reactDataGridV7';

const CLASS_NAMES = {
    CONTAINER: 'ad-slots-container',
    HEADER_TAB_WRAPPER: 'adslots-header-tab-wrapper',
    COUNTER: 'adslots-counter',
    ACTION_WRAPPER: 'adslots-actions-wrapper',
    ACTION_BTN: 'adslots-action-btn',
    SEARCH_INPUT: 'adslots-search-input',
    LIST: 'ad-slots-list',
    LIST_EDIT_MODE: 'ad-slots-list-edit-mode',
    EDIT_MODE_ROW: 'adslot-edit-mode-row',
    ROW: 'adslot-row',
};

const CONSTANT_TEXTS = {
    SEARCH_ADSLOTS: textProvider.getText('adSlots', 'serchAdslots'),
    ADD_ADSLOTS: textProvider.getText('adSlots', 'addAdslots'),
}

// Prepend SelectColumn (v7's checkbox column). The schema's `formatter` /
// `headerRenderer` props are translated to v7's `renderCell` / `renderHeaderCell`
// without modifying the schema file (other v6 grids still consume it).
// Spread + override `width`/`minWidth` to keep the legacy 60px footprint;
// v7's `SelectColumn` defaults to ~35px otherwise.
const v7Columns = [
    {...SelectColumn, width: 60, minWidth: 60},
    ...adaptColumnsForV7(adslotsGridSchema)
];

const rowKeyGetter = (row) => row.id;

class AdSlotsList extends Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedRowIds: new Set(),
            isSearchFocused: false
        };
    }

    componentDidMount() {
        if (_.isEmpty(this.props.currentGroup.adSlots)) {
            this.addNewAdSlot();
        }
    }

    getNewAdSlotData = () => {
        const {currentGroup: {adSlots, newAdSlot}} = this.props;
        if (newAdSlot && newAdSlot.length > 0) {
            return _.concat(adSlots, newAdSlot);
        }
        return adSlots;
    }

    getAdSlotsChanges = (changes) => {
        const rawData = this.getNewAdSlotData();
        return _.map(rawData, (item) => {
            const foundChange = _.get(changes, item.id);
            return foundChange ? {...item, ...foundChange} : item;
        });
    }

    onSelectedRowsChange = (selectedRowIds) => {
        // The row currently being edited must never appear as selected; v7's
        // `isRowSelectionDisabled` blocks per-row toggles but the header
        // "select all" can still include it, so strip it defensively here.
        const {isInEditMode} = this.props;
        if (isInEditMode != null && selectedRowIds.has(isInEditMode)) {
            const next = new Set(selectedRowIds);
            next.delete(isInEditMode);
            this.setState({selectedRowIds: next});
            return;
        }
        this.setState({selectedRowIds});
    }

    cancelBulkDelete = () => {
        this.setState({selectedRowIds: new Set()});
    }

    isRowSelectionDisabled = (row) => !!row && row.id === this.props.isInEditMode;

    getRowClass = (row) =>
        row && row.id === this.props.isInEditMode ? CLASS_NAMES.EDIT_MODE_ROW : CLASS_NAMES.ROW;

    addNewAdSlot = () => {
        const newAdSlot = {
            id: Date.now(),
            name: '',
            created: Date.now()
        }
        return this.props.createNewPlacementGroupAdSlot([newAdSlot]);
    };

    render () {
        const {isInEditMode, currentGroup, searchAdSlots, isPassBackFocused} = this.props;
        const {adSlotsChanges, searchTerm, customization, adSlots, err, adSlotToEdit} = currentGroup;
        const {selectedRowIds, isSearchFocused} = this.state;
        const filteredAdslots = filterAdSlots(adSlots, searchTerm, customization, adSlotToEdit);
        const adSlotsData = this.getAdSlotsChanges(adSlotsChanges);
        const rowSource = filterAdSlots(adSlotsData, searchTerm, customization, isInEditMode);
        const checkboxVisible = !!isInEditMode;
        const rowWithProps = _.map(rowSource, (row, index) => ({
            ...row,
            isPassBackFocused,
            isSearchFocused,
            rowIndex: index
        }));

        const selectedGroupIds = Array.from(selectedRowIds);

        return (
            <div className={CLASS_NAMES.CONTAINER}>
                <div className={CLASS_NAMES.HEADER_TAB_WRAPPER}>
                    <div className={CLASS_NAMES.COUNTER}>
                        {!searchTerm || _.isEmpty(searchTerm) ?
                            `Adslots (${adSlots.length})` :
                            `Adslots (${filteredAdslots.length}/${adSlots.length})`
                        }
                    </div>
                    <div className={CLASS_NAMES.ACTION_WRAPPER}>
                        <Button className={CLASS_NAMES.ACTION_BTN}
                                hookId={!_.isNil(adSlotToEdit) ? 'ad-slots-new-slot-btn-disabled' : 'ad-slots-new-slot-btn'}
                                icon='icon-add'
                                onClick={this.addNewAdSlot}
                                disabled={!_.isNil(adSlotToEdit)}
                                transparent
                        >
                            {CONSTANT_TEXTS.ADD_ADSLOTS}
                        </Button>
                        <ImportFile className={CLASS_NAMES.AD_SLOTS_IMPORT} err={err} />
                        <Input placeholder={CONSTANT_TEXTS.SEARCH_ADSLOTS}
                               icon='icon-search'
                               iconPosition='right'
                               className={CLASS_NAMES.SEARCH_INPUT}
                               onChange={(e) => searchAdSlots(e.target.value)}
                               hookId='search-adslots-input'
                               onFocus={() => this.setState({isSearchFocused: true})}
                               onBlur={() => this.setState({isSearchFocused: false})}
                        />
                    </div>
                </div>
                <div className={cx(CLASS_NAMES.LIST, !!checkboxVisible && CLASS_NAMES.LIST_EDIT_MODE)}>
                    <AutoSizer>
                        {({height, width}) => (
                            <DataGrid columns={v7Columns}
                                      rows={rowWithProps}
                                      rowKeyGetter={rowKeyGetter}
                                      selectedRows={selectedRowIds}
                                      onSelectedRowsChange={this.onSelectedRowsChange}
                                      isRowSelectionDisabled={this.isRowSelectionDisabled}
                                      rowClass={this.getRowClass}
                                      rowHeight={40}
                                      headerRowHeight={40}
                                      defaultHeight={height}
                                      defaultWidth={width}
                            />
                        )}
                    </AutoSizer>
                    {selectedGroupIds.length > 0 &&
                        (<BulkActionsOverlay cancelBulkDelete={this.cancelBulkDelete}
                                             selectedGroupIds={selectedGroupIds}
                                             displayBlockOption={false}
                                             displayDeleteOption={true}
                                             idsType='adSlotsIds'
                                             isFromAdSlots={true}
                        />)
                    }
                </div>
            </div>
        )
    }
}

AdSlotsList.propTypes = {
    currentGroup: PropTypes.object,
    isInEditMode: PropTypes.number,
    createNewPlacementGroupAdSlot: PropTypes.func,
    searchAdSlots: PropTypes.func,
    isPassBackFocused: PropTypes.bool
};

const mapStateToProps = ({placements}) => ({
    currentGroup: placements.currentGroup,
    isInEditMode: placements.currentGroup.adSlotToEdit
});

const mapDispatchToProps = ({
    createNewPlacementGroupAdSlot,
    searchAdSlots
});

export default connect(mapStateToProps, mapDispatchToProps)(AdSlotsList);
